<template>
    <div class="my-2 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div class="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2.5">
            <UIcon name="i-heroicons-shield-check" class="size-4 text-slate-500" />
            <span class="text-sm font-semibold text-slate-800">Proposed Do-Not-Contact rules</span>
            <span v-if="proposal" class="ml-auto text-xs font-medium text-slate-600">{{ statusLabel }}</span>
        </div>
        <div v-if="!proposal" class="px-4 py-3 text-xs text-slate-500">{{ part.state === 'output-error' ? 'The rules proposal could not be prepared.' : 'Preparing rules proposal…' }}</div>
        <div v-else class="space-y-3 px-4 py-3">
            <p class="text-sm font-medium text-slate-800">{{ proposal.target.kind === 'new-plan' ? proposal.target.name : `Campaign ${proposal.target.campaignId}` }}</p>
            <p class="whitespace-pre-wrap break-words text-sm text-slate-600">{{ proposal.reason }}</p>
            <div>
                <p class="text-xs font-semibold text-slate-700">Exceptions after confirmation</p>
                <ul v-if="enabledRules.length" class="mt-1 list-disc space-y-1 pl-4 text-xs text-slate-600">
                    <li v-for="rule in enabledRules" :key="rule">{{ rule }}</li>
                </ul>
                <p v-else class="mt-1 text-xs text-slate-600">None. Respect all default rules.</p>
            </div>
            <p class="text-xs leading-relaxed text-slate-500">Only the listed exceptions apply. Other company rules, individual do-not-contact, previous replies, active outreach, same-sequence checks, and campaign-specific exclusions remain protected.</p>
            <p v-if="proposal.target.kind === 'new-plan' && proposal.status !== 'consumed'" class="text-xs text-slate-600">{{ proposal.status === 'confirmed' ? 'These choices are confirmed for this plan. No campaign has been created.' : 'Confirming approves these choices for this plan only. It does not create or launch a campaign.' }}</p>
            <p v-else-if="proposal.status === 'consumed'" class="text-xs text-slate-600">These confirmed choices were used when campaign {{ proposal.campaignId }} was created. This confirmation did not authorize launch or sending.</p>
            <p v-else class="text-xs text-slate-600">{{ proposal.status === 'saved' ? 'These rules were saved to this campaign.' : 'Applying replaces this campaign’s saved rules with these choices.' }} No contact search, launch, or sending is started.</p>
            <p v-if="error" role="alert" class="text-xs text-red-700">{{ error }}</p>
            <div class="flex flex-wrap items-center gap-2">
                <UButton v-if="proposal.status === 'awaiting-confirmation'" size="sm" :disabled="!canConfirm || saving" :loading="saving" @click="confirm">
                    {{ proposal.target.kind === 'new-plan' ? 'Confirm for this plan' : 'Apply to this campaign' }}
                </UButton>
                <UButton v-if="error" color="neutral" variant="outline" size="sm" :disabled="saving" @click="emit('reload')">Reload saved proposal</UButton>
                <NuxtLink v-if="proposal.status === 'saved' && proposal.target.kind === 'existing-campaign'" :to="`/campaigns/edit/${proposal.target.campaignId}`" class="text-xs text-blue-600 hover:underline">Review campaign rules</NuxtLink>
            </div>
            <p v-if="proposal.status === 'awaiting-confirmation' && !canConfirm && !saving && !error" class="text-xs text-slate-500">Only the latest finished proposal can be confirmed. Wait for the response to finish or ask for a fresh proposal.</p>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { ConfirmCampaignEligibilityPolicyCommand } from '@coldreach/agent';

type Proposal = Awaited<ReturnType<ConfirmCampaignEligibilityPolicyCommand['execute']>>;
const props = defineProps<{ part: { state?: string; output?: unknown }; canConfirm: boolean; saving?: boolean; error?: string | null }>();
const emit = defineEmits<{ confirm: []; reload: [] }>();
const proposal = computed(() => {
    if (props.part.state !== 'output-available' || !props.part.output || typeof props.part.output !== 'object') return null;
    // Render only a finished server output, never an in-progress tool input.
    const output = props.part.output as Proposal;
    return output.proposalId && output.target && output.policy ? output : null;
});
const enabledRules = computed(() => {
    const policy = proposal.value?.policy;
    if (!policy) return [];
    return [
        policy.allowRecentCooldownContacts ? 'Recent outreach cooldown' : null,
        policy.allowManuallyExcludedCompanies ? 'All companies on the manual exclusion list, including future matches' : null,
        policy.allowCrmDealCompanies ? 'All companies linked to non-ignored imported CRM deals at any stage, including future matches' : null,
    ].filter((label): label is string => label !== null);
});
const statusLabel = computed(() => ({
    'awaiting-confirmation': 'Needs confirmation',
    confirmed: 'Confirmed for plan · not created',
    saved: 'Saved to campaign',
    consumed: 'Used to create campaign',
})[proposal.value!.status]);
function confirm() {
    if (props.canConfirm && !props.saving && proposal.value?.status === 'awaiting-confirmation') emit('confirm');
}
</script>
